import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started', color: 'bg-slate-100 text-slate-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-amber-100 text-amber-700' },
  { value: 'delayed', label: 'Delayed', color: 'bg-red-100 text-red-700' }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-600' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  { value: 'high', label: 'High', color: 'bg-red-100 text-red-700' }
];

const GANTT_VIEW_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' }
];

export const ScheduleTab = ({ projectId }) => {
  const [items, setItems] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'gantt'
  const [ganttTimeScale, setGanttTimeScale] = useState('weeks'); // 'days', 'weeks', 'months'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportResultModal, setShowImportResultModal] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importMode, setImportMode] = useState('append');
  const [importing, setImporting] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    task_number: '',
    description: '',
    start_date: '',
    end_date: '',
    progress: 0,
    assigned_to: '',
    priority: 'medium',
    status: 'not_started',
    notes: ''
  });
  const { user } = useAuth();

  useEffect(() => {
    fetchScheduleItems();
    fetchUsers();
  }, [projectId]);

  const fetchScheduleItems = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/schedule`);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load schedule');
    }
  };

  const fetchUsers = async () => {
    try {
      // Use project-specific users endpoint for non-admin users
      const response = await axios.get(`${API}/projects/${projectId}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const createOrUpdateItem = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.patch(`${API}/schedule/${editingItem.id}`, formData);
        toast.success('Task updated!');
      } else {
        await axios.post(`${API}/projects/${projectId}/schedule`, formData);
        toast.success('Task created!');
      }
      setShowCreateModal(false);
      setEditingItem(null);
      resetForm();
      fetchScheduleItems();
    } catch (error) {
      toast.error(editingItem ? 'Failed to update task' : 'Failed to create task');
    }
  };

  const editItem = (item) => {
    setFormData({
      task_number: item.task_number,
      description: item.description,
      start_date: item.start_date,
      end_date: item.end_date,
      progress: item.progress,
      assigned_to: item.assigned_to || '',
      priority: item.priority,
      status: item.status,
      notes: item.notes || ''
    });
    setEditingItem(item);
    setShowCreateModal(true);
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API}/schedule/${itemId}`);
      toast.success('Task deleted');
      fetchScheduleItems();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const updateProgress = async (itemId, progress) => {
    try {
      const status = progress === 100 ? 'completed' : progress > 0 ? 'in_progress' : 'not_started';
      await axios.patch(`${API}/schedule/${itemId}`, { progress, status });
      fetchScheduleItems();
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const resetForm = () => {
    setFormData({
      task_number: '',
      description: '',
      start_date: '',
      end_date: '',
      progress: 0,
      assigned_to: '',
      priority: 'medium',
      status: 'not_started',
      notes: ''
    });
  };

  const getStatusStyle = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status);
    return opt ? opt.color : 'bg-slate-100 text-slate-700';
  };

  const getPriorityStyle = (priority) => {
    const opt = PRIORITY_OPTIONS.find(p => p.value === priority);
    return opt ? opt.color : 'bg-slate-100 text-slate-600';
  };

  const exportToExcel = () => {
    import('xlsx').then(XLSX => {
      const exportData = items.map(item => ({
        'Task #': item.task_number,
        'Description': item.description,
        'Start Date': item.start_date,
        'End Date': item.end_date,
        'Progress': `${item.progress}%`,
        'Status': item.status.replace('_', ' '),
        'Priority': item.priority,
        'Assigned To': item.assigned_to_name || '-'
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Schedule');
      XLSX.writeFile(wb, 'project_schedule.xlsx');
      toast.success('Exported to Excel');
    });
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/schedule/template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'schedule_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/schedule/import?mode=${importMode}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        // Store the result and show the result modal
        setImportResult(response.data);
        setShowImportModal(false);
        setShowImportResultModal(true);
        fetchScheduleItems();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import schedule');
    } finally {
      setImporting(false);
      e.target.value = ''; // Reset file input
    }
  };

  // Calculate Gantt chart dimensions based on time scale
  const getGanttData = () => {
    if (items.length === 0) {
      const today = new Date();
      return { minDate: today, maxDate: today, totalUnits: 12, unitType: ganttTimeScale };
    }
    
    const dates = items.flatMap(item => [new Date(item.start_date), new Date(item.end_date)]);
    let minDate = new Date(Math.min(...dates));
    let maxDate = new Date(Math.max(...dates));
    
    // Adjust dates based on time scale
    if (ganttTimeScale === 'weeks') {
      // Start from beginning of week (Sunday)
      minDate.setDate(minDate.getDate() - minDate.getDay());
      // End at end of week (Saturday)
      maxDate.setDate(maxDate.getDate() + (6 - maxDate.getDay()));
    } else if (ganttTimeScale === 'months') {
      // Start from beginning of month
      minDate.setDate(1);
      // End at end of month
      maxDate = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0);
    }
    
    let totalUnits;
    if (ganttTimeScale === 'days') {
      totalUnits = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) + 1;
      totalUnits = Math.max(totalUnits, 30); // Minimum 30 days
    } else if (ganttTimeScale === 'weeks') {
      totalUnits = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24 * 7)) + 1;
      totalUnits = Math.max(totalUnits, 12); // Minimum 12 weeks
    } else {
      // months
      totalUnits = (maxDate.getFullYear() - minDate.getFullYear()) * 12 + (maxDate.getMonth() - minDate.getMonth()) + 1;
      totalUnits = Math.max(totalUnits, 6); // Minimum 6 months
    }
    
    return { minDate, maxDate, totalUnits, unitType: ganttTimeScale };
  };

  const getBarPosition = (item, ganttData) => {
    const start = new Date(item.start_date);
    const end = new Date(item.end_date);
    
    let startOffset, duration, totalUnits;
    
    if (ganttData.unitType === 'days') {
      startOffset = Math.ceil((start - ganttData.minDate) / (1000 * 60 * 60 * 24));
      duration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      totalUnits = ganttData.totalUnits;
    } else if (ganttData.unitType === 'weeks') {
      startOffset = Math.ceil((start - ganttData.minDate) / (1000 * 60 * 60 * 24 * 7));
      duration = Math.ceil((end - start) / (1000 * 60 * 60 * 24 * 7)) + 0.5;
      totalUnits = ganttData.totalUnits;
    } else {
      // months
      const startMonth = (start.getFullYear() - ganttData.minDate.getFullYear()) * 12 + (start.getMonth() - ganttData.minDate.getMonth());
      const endMonth = (end.getFullYear() - ganttData.minDate.getFullYear()) * 12 + (end.getMonth() - ganttData.minDate.getMonth());
      startOffset = startMonth;
      duration = endMonth - startMonth + 1;
      totalUnits = ganttData.totalUnits;
    }
    
    const leftPercent = (startOffset / totalUnits) * 100;
    const widthPercent = (duration / totalUnits) * 100;
    
    return { left: `${Math.max(leftPercent, 0)}%`, width: `${Math.max(widthPercent, 3)}%` };
  };

  // Generate timeline headers based on time scale
  const getTimelineHeaders = (ganttData) => {
    const headers = [];
    const currentDate = new Date(ganttData.minDate);
    
    if (ganttData.unitType === 'days') {
      for (let i = 0; i < Math.min(ganttData.totalUnits, 60); i++) {
        const date = new Date(currentDate);
        date.setDate(currentDate.getDate() + i);
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
        headers.push({
          label: date.getDate().toString(),
          subLabel: date.toLocaleDateString('en', { month: 'short' }),
          isWeekend,
          date
        });
      }
    } else if (ganttData.unitType === 'weeks') {
      for (let i = 0; i < Math.min(ganttData.totalUnits, 26); i++) {
        const weekStart = new Date(currentDate);
        weekStart.setDate(currentDate.getDate() + (i * 7));
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        headers.push({
          label: `W${i + 1}`,
          subLabel: `${weekStart.toLocaleDateString('en', { month: 'short', day: 'numeric' })}`,
          isWeekend: false,
          date: weekStart
        });
      }
    } else {
      // months
      for (let i = 0; i < Math.min(ganttData.totalUnits, 24); i++) {
        const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
        headers.push({
          label: monthDate.toLocaleDateString('en', { month: 'short' }),
          subLabel: monthDate.getFullYear().toString(),
          isWeekend: false,
          date: monthDate
        });
      }
    }
    
    return headers;
  };

  const ganttData = getGanttData();
  const timelineHeaders = getTimelineHeaders(ganttData);

  return (
    <div className="space-y-4" data-testid="schedule-tab">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Project Schedule</h2>
          <p className="text-sm text-slate-600 mt-1">Manage tasks and track progress</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-slate-100 rounded-md p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${viewMode === 'table' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="view-table-button"
            >
              Table
            </button>
            <button
              onClick={() => setViewMode('gantt')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${viewMode === 'gantt' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="view-gantt-button"
            >
              Gantt
            </button>
          </div>
          
          {/* Time Scale Toggle (only visible in Gantt view) */}
          {viewMode === 'gantt' && (
            <div className="flex items-center bg-slate-100 rounded-md p-1" data-testid="gantt-timescale-toggle">
              {GANTT_VIEW_OPTIONS.map(option => (
                <button
                  key={option.value}
                  onClick={() => setGanttTimeScale(option.value)}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${ganttTimeScale === option.value ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                  data-testid={`gantt-scale-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
          
          <button 
            onClick={() => setShowImportModal(true)} 
            className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50"
            data-testid="import-schedule-button"
          >
            Import CSV
          </button>
          <button onClick={exportToExcel} className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50">
            Export Excel
          </button>
          <button
            onClick={() => { setEditingItem(null); resetForm(); setShowCreateModal(true); }}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
            data-testid="add-task-button"
          >
            Add Task
          </button>
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-auto">
          <table className="w-full" data-testid="schedule-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Task #</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Description</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Start</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">End</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Progress</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Priority</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b hover:bg-slate-50" data-testid={`schedule-row-${item.id}`}>
                  <td className="p-4 font-mono text-sm text-indigo-600">{item.task_number}</td>
                  <td className="p-4">
                    <div className="font-medium text-slate-900">{item.description}</div>
                    {item.notes && <div className="text-xs text-slate-500 mt-1">{item.notes}</div>}
                  </td>
                  <td className="p-4 text-sm">{new Date(item.start_date).toLocaleDateString()}</td>
                  <td className="p-4 text-sm">{new Date(item.end_date).toLocaleDateString()}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${item.progress === 100 ? 'bg-green-500' : 'bg-indigo-500'}`}
                          style={{ width: `${item.progress}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-slate-600">{item.progress}%</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusStyle(item.status)}`}>
                      {item.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityStyle(item.priority)}`}>
                      {item.priority}
                    </span>
                  </td>
                  <td className="p-4 text-sm">{item.assigned_to_name || '-'}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => editItem(item)} className="text-sm text-blue-600 hover:underline">Edit</button>
                      <button onClick={() => deleteItem(item.id)} className="text-sm text-red-600 hover:underline">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan="9" className="p-8 text-center text-slate-500">
                    No tasks scheduled yet. Click "Add Task" to create your first task.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Gantt View */}
      {viewMode === 'gantt' && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-auto">
          <div className="min-w-[1000px]">
            {/* Timeline Header */}
            <div className="flex border-b bg-slate-50">
              <div className="w-64 p-4 font-semibold text-slate-700 border-r flex-shrink-0">
                <div>Task</div>
                <div className="text-xs font-normal text-slate-500 mt-1">
                  View: {ganttTimeScale.charAt(0).toUpperCase() + ganttTimeScale.slice(1)}
                </div>
              </div>
              <div className="flex-1 relative">
                <div className="flex">
                  {timelineHeaders.map((header, i) => (
                    <div 
                      key={i} 
                      className={`flex-1 text-center py-2 text-xs border-r ${header.isWeekend ? 'bg-slate-100' : ''}`}
                      style={{ minWidth: ganttTimeScale === 'days' ? '30px' : ganttTimeScale === 'weeks' ? '80px' : '100px' }}
                    >
                      <div className="font-semibold text-slate-700">{header.label}</div>
                      <div className="text-slate-400">{header.subLabel}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Gantt Rows */}
            {items.map((item) => {
              const barPos = getBarPosition(item, ganttData);
              return (
                <div key={item.id} className="flex border-b hover:bg-slate-50" data-testid={`gantt-row-${item.id}`}>
                  <div className="w-64 p-3 border-r flex-shrink-0">
                    <div className="font-medium text-sm text-slate-900 truncate">{item.task_number}</div>
                    <div className="text-xs text-slate-500 truncate">{item.description}</div>
                  </div>
                  <div className="flex-1 relative h-16 flex items-center">
                    {/* Grid lines for time units */}
                    <div className="absolute inset-0 flex">
                      {timelineHeaders.map((_, i) => (
                        <div 
                          key={i} 
                          className="flex-1 border-r border-slate-100"
                          style={{ minWidth: ganttTimeScale === 'days' ? '30px' : ganttTimeScale === 'weeks' ? '80px' : '100px' }}
                        />
                      ))}
                    </div>
                    {/* Task Bar */}
                    <div
                      className="absolute h-8 rounded-md shadow-sm cursor-pointer group z-10"
                      style={{
                        left: barPos.left,
                        width: barPos.width,
                        backgroundColor: item.status === 'completed' ? '#10B981' : 
                                        item.status === 'delayed' ? '#EF4444' : 
                                        item.status === 'in_progress' ? '#6366F1' : '#94A3B8'
                      }}
                      onClick={() => editItem(item)}
                    >
                      {/* Progress fill */}
                      <div 
                        className="absolute inset-0 bg-black/20 rounded-md"
                        style={{ width: `${item.progress}%` }}
                      />
                      {/* Label */}
                      <div className="absolute inset-0 flex items-center px-2 text-white text-xs font-medium truncate">
                        {item.progress}%
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {items.length === 0 && (
              <div className="p-12 text-center text-slate-500">
                No tasks to display. Add tasks to see the Gantt chart.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="task-modal">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              {editingItem ? 'Edit Task' : 'Add Task'}
            </h2>
            <form onSubmit={createOrUpdateItem} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Task Number</label>
                  <input
                    type="text"
                    value={formData.task_number}
                    onChange={(e) => setFormData({...formData, task_number: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    placeholder="Auto-generated if empty"
                    data-testid="task-number-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Assigned To</label>
                  <select
                    value={formData.assigned_to}
                    onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    data-testid="task-assignee-select"
                  >
                    <option value="">Unassigned</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    required
                    placeholder="Task description"
                    data-testid="task-description-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Start Date *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    required
                    data-testid="task-start-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">End Date *</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    required
                    data-testid="task-end-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    data-testid="task-priority-select"
                  >
                    {PRIORITY_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    data-testid="task-status-select"
                  >
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Progress: {formData.progress}%</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={formData.progress}
                    onChange={(e) => setFormData({...formData, progress: parseInt(e.target.value)})}
                    className="w-full"
                    data-testid="task-progress-slider"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    rows="2"
                    placeholder="Additional notes..."
                    data-testid="task-notes-input"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 bg-indigo-600 text-white py-2 rounded-md hover:bg-indigo-700" data-testid="submit-task-button">
                  {editingItem ? 'Update Task' : 'Create Task'}
                </button>
                <button type="button" onClick={() => { setShowCreateModal(false); setEditingItem(null); }} className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowImportModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="import-modal">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Import Schedule</h2>
            
            <div className="space-y-4">
              {/* Download Template */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-2">Step 1: Download Template</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Download the CSV template, fill in your schedule data, and upload it below.
                </p>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-md hover:bg-slate-100 text-sm font-medium"
                  data-testid="download-template-button"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Template (CSV)
                </button>
              </div>

              {/* Import Mode */}
              <div>
                <h3 className="font-medium text-slate-900 mb-2">Step 2: Choose Import Mode</h3>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="importMode"
                      value="append"
                      checked={importMode === 'append'}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="text-indigo-600"
                    />
                    <span className="text-sm">
                      <strong>Append</strong> - Add to existing tasks
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="importMode"
                      value="replace"
                      checked={importMode === 'replace'}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="text-indigo-600"
                    />
                    <span className="text-sm">
                      <strong>Replace</strong> - Clear and import new
                    </span>
                  </label>
                </div>
                {importMode === 'replace' && (
                  <p className="text-xs text-amber-600 mt-2">
                    Warning: This will delete all existing tasks before importing.
                  </p>
                )}
              </div>

              {/* Upload File */}
              <div>
                <h3 className="font-medium text-slate-900 mb-2">Step 3: Upload CSV File</h3>
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 hover:border-indigo-500 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className="w-8 h-8 mb-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-sm text-slate-600">
                      {importing ? 'Importing...' : 'Click to upload CSV file'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">CSV files only</p>
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileImport}
                    className="hidden"
                    disabled={importing}
                    data-testid="import-file-input"
                  />
                </label>
              </div>

              {/* Template Format Info */}
              <div className="bg-slate-50 rounded-lg p-4 text-sm">
                <h4 className="font-medium text-slate-900 mb-2">CSV Format</h4>
                <p className="text-slate-600 mb-2">Required columns:</p>
                <ul className="text-slate-600 space-y-1 text-xs">
                  <li>• <code className="bg-white px-1 rounded">description</code> - Task description (required)</li>
                  <li>• <code className="bg-white px-1 rounded">start_date</code> - Format: YYYY-MM-DD (required)</li>
                  <li>• <code className="bg-white px-1 rounded">end_date</code> - Format: YYYY-MM-DD (required)</li>
                  <li>• <code className="bg-white px-1 rounded">task_number</code> - Auto-generated if empty</li>
                  <li>• <code className="bg-white px-1 rounded">progress</code> - 0-100</li>
                  <li>• <code className="bg-white px-1 rounded">priority</code> - low, medium, high</li>
                  <li>• <code className="bg-white px-1 rounded">status</code> - not_started, in_progress, completed</li>
                  <li>• <code className="bg-white px-1 rounded">notes</code> - Optional notes</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setShowImportModal(false)}
                className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Result Modal */}
      {showImportResultModal && importResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowImportResultModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="import-result-modal">
            {/* Header */}
            <div className="flex items-start gap-4 mb-4">
              {importResult.total_errors === 0 ? (
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              )}
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {importResult.total_errors === 0 ? 'Import Successful' : 'Import Completed with Errors'}
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  {importResult.imported_count} task{importResult.imported_count !== 1 ? 's' : ''} imported successfully
                  {importResult.total_errors > 0 && `, ${importResult.total_errors} row${importResult.total_errors !== 1 ? 's' : ''} failed`}
                </p>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-600">{importResult.imported_count}</div>
                <div className="text-xs text-green-700 font-medium">Imported</div>
              </div>
              <div className={`rounded-lg p-3 text-center ${importResult.total_errors > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
                <div className={`text-2xl font-bold ${importResult.total_errors > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                  {importResult.total_errors}
                </div>
                <div className={`text-xs font-medium ${importResult.total_errors > 0 ? 'text-red-700' : 'text-slate-500'}`}>
                  Errors
                </div>
              </div>
            </div>

            {/* Error Details */}
            {importResult.errors && importResult.errors.length > 0 && (
              <div className="flex-1 overflow-hidden flex flex-col">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Error Details
                  {importResult.total_errors > 10 && (
                    <span className="text-xs font-normal text-slate-500">(showing first 10)</span>
                  )}
                </h3>
                <div className="flex-1 overflow-y-auto bg-slate-50 rounded-lg p-3 space-y-2">
                  {importResult.errors.map((error, index) => (
                    <div key={index} className="flex items-start gap-2 text-sm">
                      <span className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        !
                      </span>
                      <span className="text-slate-700">{error}</span>
                    </div>
                  ))}
                </div>
                {importResult.total_errors > 10 && (
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    ... and {importResult.total_errors - 10} more error{importResult.total_errors - 10 !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            )}

            {/* Tips for fixing errors */}
            {importResult.total_errors > 0 && (
              <div className="bg-blue-50 rounded-lg p-3 mt-4 text-sm">
                <h4 className="font-medium text-blue-900 mb-1">Tips to fix errors:</h4>
                <ul className="text-blue-700 space-y-1 text-xs">
                  <li>• Ensure dates are in YYYY-MM-DD format (e.g., 2026-03-15)</li>
                  <li>• Description, start_date, and end_date are required fields</li>
                  <li>• Progress should be a number between 0-100</li>
                  <li>• Download the template for the correct format</li>
                </ul>
              </div>
            )}

            {/* Close Button */}
            <div className="flex gap-3 pt-4 mt-4 border-t">
              <button
                onClick={() => {
                  setShowImportResultModal(false);
                  setImportResult(null);
                }}
                className="flex-1 bg-indigo-600 text-white py-2 rounded-md hover:bg-indigo-700"
              >
                Done
              </button>
              {importResult.total_errors > 0 && (
                <button
                  onClick={() => {
                    setShowImportResultModal(false);
                    setImportResult(null);
                    setShowImportModal(true);
                  }}
                  className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
